// The second, LOAD-BEARING default-deny gate (Flow 3; D32 "Build proceeds only on
// approval"; docs/phase-0/slice5-component-designs.md §3.3 `.approval-gate`). A
// standing panel below the mockup gallery, visually distinct from the gallery cards so
// it never reads as "just another card" - the approve action is the ONLY door to Build
// and states plainly what approving means. Approval itself requires an explicit
// lightweight confirm (`BuildConfirmDialog`) - never committed from the first tap.
import { useId, useRef, useState } from "react";
import { buildCopy } from "../copy/build.js";
import { BuildConfirmDialog } from "./BuildConfirmDialog.js";

export const BrandApprovalGate = ({
  appName,
  disabledReason,
  tweakInFlightReason,
  onApprove,
  onKeepRefining,
}: {
  readonly appName: string;
  /** Present only for a viewer - the approval gate is never actionable by a viewer
   * (ux spec §6.4 "the one moment that commits real build spend"). */
  readonly disabledReason?: string;
  /** Present while any mockup's own tweak is in flight - the button stays enabled-
   * looking (never a silent disabled state, ux spec §6.6) with this note attached via
   * `aria-describedby`. */
  readonly tweakInFlightReason?: string;
  readonly onApprove: () => void;
  readonly onKeepRefining: () => void;
}): React.JSX.Element => {
  const [confirming, setConfirming] = useState(false);
  const approveButtonRef = useRef<HTMLButtonElement>(null);
  const reasonId = useId();
  const tweakNoteId = useId();
  const disabled = disabledReason !== undefined;

  return (
    <div
      className="card pad approval-gate"
      role="region"
      aria-label="Approve this look"
      data-testid="brand-approval-gate"
    >
      <p style={{ margin: 0 }}>{buildCopy("build.brand.mockups.gate.body")}</p>
      <div className="row gap12 mt12 wrap-g">
        <button
          type="button"
          className="btn btn-primary"
          ref={approveButtonRef}
          disabled={disabled}
          aria-describedby={
            disabled ? reasonId : tweakInFlightReason !== undefined ? tweakNoteId : undefined
          }
          onClick={() => {
            setConfirming(true);
          }}
        >
          {buildCopy("build.brand.mockups.gate.approveCta")}
        </button>
        <button
          type="button"
          className="btn btn-ghost"
          disabled={disabled}
          onClick={onKeepRefining}
        >
          {buildCopy("build.brand.mockups.gate.keepRefiningCta")}
        </button>
      </div>
      {disabled ? (
        <p className="muted mt8" id={reasonId} style={{ fontSize: 12.5 }}>
          {disabledReason}
        </p>
      ) : tweakInFlightReason !== undefined ? (
        <p className="muted mt8" id={tweakNoteId} style={{ fontSize: 12.5 }}>
          {tweakInFlightReason}
        </p>
      ) : null}
      {confirming ? (
        <BuildConfirmDialog
          heading={buildCopy("build.brand.mockups.gate.approveCta")}
          body={buildCopy("build.brand.mockups.gate.confirmBody", { appName })}
          confirmLabel={buildCopy("build.brand.mockups.gate.approveCta")}
          cancelLabel="Cancel"
          triggerRef={approveButtonRef}
          onConfirm={() => {
            setConfirming(false);
            onApprove();
          }}
          onCancel={() => {
            setConfirming(false);
          }}
        />
      ) : null}
    </div>
  );
};
